import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useState } from "react";
import { useOptionalAutoScrollContext } from "./AutoScrollContainer.tsx";
import { DO_NOT_RENDER_ID_PREFIX } from "./ensure-tool-responses.ts";
import { groupToolMessages } from "./group-tool-messages";
import {
  DEFAULT_HITL_REJECT_MESSAGE,
  getUserMessageFromHumanMessageRejectDecisionMessage,
} from "./human-in-the-loop.ts";
import { matchPendingReviewsToCalls } from "./match-pending-reviews";
import { AIMessage, getAIMessageRenderState } from "./messages/AIMessage.tsx";
import { AssistantMessageLoading } from "./messages/AssistantMessageLoading.tsx";
import { HumanMessage } from "./messages/HumanMesage.tsx";
import { ToolGroup } from "./messages/ToolGroup.tsx";
import { ToolCallName } from "./messages/utils/tool-calls.ts";
import { usePendingToolReviews } from "./PendingToolReviewScope";
import { useStreamContext } from "./Stream.tsx";
import {
  type ChatMessage,
  type ChatToolMessage,
  getChatMessageType,
} from "./types.ts";
import { getContentString, mapToolResultToToolCall } from "./utils/index.tsx";

const ESTIMATED_MESSAGE_HEIGHT = 180;
const INITIAL_VIRTUALIZER_RECT = { width: 0, height: 720 };
const MESSAGE_OVERSCAN = 8;
const LANGCHAIN_DEFAULT_REJECT_MESSAGE_PREFIX =
  "User rejected the tool call for ";
const TOOL_EXECUTION_ERROR_PREFIX = "Error executing tool ";

const HUMAN_READABLE_HITL_REJECT_TOOL_NAMES = new Set<string>([
  ToolCallName.EDIT_DATA_KEY,
  ToolCallName.LEGACY_WRITE_DATA_KEY,
  ToolCallName.EDIT_CUSTOM_DOCUMENT_TEXT,
]);

interface MessagesListProps {
  threadsLoading: boolean;
  threadsLength: number;
  firstTokenReceived: boolean;
  messages?: ChatMessage[];
  connectionNames?: string[];
}

const isSerializedLangChainConstructor = (message: ChatMessage) =>
  (message as any).lc === 1 &&
  message.type === "constructor" &&
  Array.isArray(message.id) &&
  Object.prototype.hasOwnProperty.call(message, "kwargs");

const addToolCallName = (
  toolCallNameById: Map<string, string>,
  toolCall: unknown
) => {
  if (
    typeof toolCall !== "object" ||
    toolCall === null ||
    Array.isArray(toolCall)
  ) {
    return;
  }

  const id = (toolCall as { id?: unknown }).id;
  const name = (toolCall as { name?: unknown }).name;
  if (typeof id === "string" && typeof name === "string") {
    toolCallNameById.set(id, name);
  }
};

const getToolCallNameById = (messages: ChatMessage[]) => {
  const toolCallNameById = new Map<string, string>();

  messages.forEach((message) => {
    if (getChatMessageType(message) !== "ai") {
      return;
    }

    if (Array.isArray(message.tool_calls)) {
      message.tool_calls.forEach((toolCall) =>
        addToolCallName(toolCallNameById, toolCall)
      );
    }

    if (Array.isArray(message.content)) {
      message.content
        .filter(
          (block) =>
            block.type === "tool_call" ||
            block.type === "tool_call_chunk" ||
            block.type === "invalid_tool_call"
        )
        .forEach((toolCall) => addToolCallName(toolCallNameById, toolCall));
    }
  });

  return toolCallNameById;
};

const getHumanReadableHitlRejectFeedback = (
  message: ChatMessage,
  toolCallNameById: ReadonlyMap<string, string>
) => {
  if (getChatMessageType(message) !== "tool" || message.status !== "error") {
    return null;
  }

  const content = getContentString(message.content).trim();
  const structuredFeedback =
    getUserMessageFromHumanMessageRejectDecisionMessage(content);
  if (structuredFeedback) {
    return structuredFeedback;
  }

  const toolName =
    message.name ??
    (message.tool_call_id
      ? toolCallNameById.get(message.tool_call_id)
      : undefined);
  if (
    typeof toolName !== "string" ||
    !HUMAN_READABLE_HITL_REJECT_TOOL_NAMES.has(toolName)
  ) {
    return null;
  }

  if (
    content.length === 0 ||
    content === DEFAULT_HITL_REJECT_MESSAGE ||
    content.startsWith(LANGCHAIN_DEFAULT_REJECT_MESSAGE_PREFIX) ||
    content.startsWith(TOOL_EXECUTION_ERROR_PREFIX)
  ) {
    return null;
  }

  return content;
};

const withHumanReadableHitlRejectMessages = (messages: ChatMessage[]) => {
  const toolCallNameById = getToolCallNameById(messages);
  const transcriptMessages: ChatMessage[] = [];
  const recentFeedback = new Set<string>();

  messages.forEach((message, index) => {
    const messageType = getChatMessageType(message);
    if (
      messageType === "human" ||
      (messageType === "ai" &&
        !message.tool_calls?.length &&
        getContentString(message.content).trim().length > 0)
    ) {
      recentFeedback.clear();
    }

    const feedback = getHumanReadableHitlRejectFeedback(
      message,
      toolCallNameById
    );
    if (feedback && !recentFeedback.has(feedback)) {
      transcriptMessages.push({
        id: `hitl-reject-feedback-${
          message.id ?? message.tool_call_id ?? index
        }`,
        type: "human",
        role: "user",
        content: feedback,
      });
      recentFeedback.add(feedback);
    }

    transcriptMessages.push(message);
  });

  return transcriptMessages;
};

export const MessagesList = function MessagesList({
  threadsLoading,
  threadsLength,
  firstTokenReceived,
  messages: messageOverride,
  connectionNames,
}: MessagesListProps) {
  const {
    messages: streamMessages,
    isLoading,
    isThreadLoading,
    subagents,
  } = useStreamContext();
  const pendingReviews = usePendingToolReviews();
  const messages = messageOverride ?? streamMessages;
  const autoScrollContext = useOptionalAutoScrollContext();
  const scrollElement = autoScrollContext?.scrollElement ?? null;

  const toolResultsMappedToToolCall = useMemo(
    () => mapToolResultToToolCall(messages),
    [messages]
  );
  const transcriptMessages = useMemo(
    () => withHumanReadableHitlRejectMessages(messages),
    [messages]
  );

  const filteredMessages = useMemo(
    () =>
      transcriptMessages.filter(
        (m) =>
          !m.id?.startsWith(DO_NOT_RENDER_ID_PREFIX) &&
          !isSerializedLangChainConstructor(m)
      ),
    [transcriptMessages]
  );
  const entries = useMemo(
    () => groupToolMessages(filteredMessages),
    [filteredMessages]
  );
  const resultsByCall = useMemo(
    () =>
      new Map<string, ChatToolMessage>(
        filteredMessages.flatMap((message) =>
          getChatMessageType(message) === "tool" && message.tool_call_id
            ? [
                [
                  message.tool_call_id,
                  {
                    id: message.id,
                    type: "tool" as const,
                    role: "tool" as const,
                    tool_call_id: message.tool_call_id,
                    name: message.name,
                    status: message.status,
                    content: message.content,
                    artifact: message.artifact,
                  },
                ] as const,
              ]
            : []
        )
      ),
    [filteredMessages]
  );
  const directReviews = useMemo(
    () => pendingReviews.filter((review) => !review.subagentId),
    [pendingReviews]
  );
  const reviewByCall = useMemo(
    () =>
      matchPendingReviewsToCalls(
        entries.flatMap((entry) => (entry.type === "group" ? entry.calls : [])),
        directReviews,
        (id) => resultsByCall.has(id)
      ),
    [entries, directReviews, resultsByCall]
  );
  const [manualExpansion, setManualExpansion] = useState<
    Record<string, boolean>
  >({});
  const [decidedCalls, setDecidedCalls] = useState<
    Record<string, "approve" | "reject" | "edit">
  >({});
  useEffect(() => {
    const additions: Record<string, "approve" | "reject" | "edit"> = {};
    reviewByCall.forEach((reviewKey, callId) => {
      const decision = directReviews.find(
        (review) => review.key === reviewKey
      )?.decision;
      if (decision) additions[callId] = decision;
    });
    if (Object.keys(additions).length)
      setDecidedCalls((current) =>
        Object.entries(additions).every(
          ([id, decision]) => current[id] === decision
        )
          ? current
          : { ...current, ...additions }
      );
  }, [reviewByCall, directReviews]);
  const decisions = useMemo(
    () => new Map(Object.entries(decidedCalls)),
    [decidedCalls]
  );
  const pendingReviewByCall = useMemo(
    () =>
      new Map(
        [...reviewByCall].filter(
          ([, key]) =>
            !directReviews.find((review) => review.key === key)?.decision
        )
      ),
    [reviewByCall, directReviews]
  );

  const lastEntry = entries[entries.length - 1];
  // Approval controls are portals owned by these rows. Keep their owners and
  // ancestor delegations mounted even when the user scrolls away.
  const approvalOwnerIndices = useMemo(() => {
    if (pendingReviews.length === 0) return [];
    const ownerIds = new Set(
      [...subagents.values()]
        .filter((agent) =>
          pendingReviews.some(
            ({ namespace, decision }) =>
              !decision &&
              agent.namespace.length > 0 &&
              agent.namespace.every((part, index) => namespace[index] === part)
          )
        )
        .map((agent) => agent.id)
    );
    return entries.flatMap((entry, index) => {
      if (entry.type !== "group") return [];
      const ownsReview = entry.calls.some(
        (call) => pendingReviewByCall.has(call.id) || ownerIds.has(call.id)
      );
      if (!ownsReview) return [];
      const previous = entries[index - 1];
      return previous?.type === "message" &&
        previous.messageIndex === entry.firstMessageIndex
        ? [index - 1, index]
        : [index];
    });
  }, [pendingReviews, subagents, entries, pendingReviewByCall]);
  const lastVisibleMessage =
    lastEntry?.type === "message" ? lastEntry.message : undefined;
  const lastVisibleMessageShowsActiveProgress = useMemo(() => {
    if (lastEntry?.type === "group") {
      return true;
    }
    if (
      !lastVisibleMessage ||
      getChatMessageType(lastVisibleMessage) !== "ai"
    ) {
      return false;
    }

    const renderState = getAIMessageRenderState(
      lastVisibleMessage,
      lastVisibleMessage.id
        ? toolResultsMappedToToolCall.get(lastVisibleMessage.id)
        : undefined
    );

    return (
      renderState.textContent.length > 0 ||
      renderState.hasThinkingMessages ||
      renderState.hasPendingVisibleToolCall
    );
  }, [lastEntry, lastVisibleMessage, toolResultsMappedToToolCall]);
  const showAssistantLoading =
    isLoading &&
    lastEntry?.type !== "group" &&
    (!firstTokenReceived || !lastVisibleMessageShowsActiveProgress);
  const rowVirtualizer = useVirtualizer({
    count: entries.length,
    estimateSize: () => ESTIMATED_MESSAGE_HEIGHT,
    getItemKey: (index) => {
      return entries[index].key;
    },
    getScrollElement: () => scrollElement,
    initialRect: INITIAL_VIRTUALIZER_RECT,
    measureElement: (element) =>
      element.getBoundingClientRect().height || ESTIMATED_MESSAGE_HEIGHT,
    overscan: MESSAGE_OVERSCAN,
    rangeExtractor: (range) =>
      [
        ...new Set([...defaultRangeExtractor(range), ...approvalOwnerIndices]),
      ].sort((a, b) => a - b),
  });

  if ((threadsLoading && threadsLength === 0) || isThreadLoading) {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-end justify-end gap-4">
          <div className="h-10 w-1/2 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="flex flex-col items-start gap-4">
          <div className="h-22 w-2/3 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-1/3 animate-pulse rounded bg-gray-200" />
          <div className="h-16 w-5/6 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-1/3 animate-pulse rounded bg-gray-200" />
          <div className="h-22 w-2/3 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-1/3 animate-pulse rounded bg-gray-200" />
          <div className="h-14 w-2/5 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-5/6 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="relative w-full"
        data-testid="messages-virtualizer"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const entry = entries[virtualItem.index];
          const message = entry.type === "message" ? entry.message : undefined;
          const messageType = message ? getChatMessageType(message) : undefined;
          const isLastMessage = entry === lastEntry;
          const childPending =
            entry.type === "group" &&
            entry.calls.some((call) => {
              const agent = subagents.get(call.id);
              return (
                agent &&
                pendingReviews.some(
                  ({ namespace, decision }) =>
                    !decision &&
                    agent.namespace.length > 0 &&
                    agent.namespace.every(
                      (part, index) => namespace[index] === part
                    )
                )
              );
            });
          const groupReviews =
            entry.type === "group"
              ? new Map(
                  entry.calls.flatMap((call) =>
                    pendingReviewByCall.has(call.id)
                      ? [[call.id, pendingReviewByCall.get(call.id)!] as const]
                      : []
                  )
                )
              : new Map<string, string>();
          const groupPending = groupReviews.size > 0 || childPending;
          const groupExpanded =
            entry.type === "group" &&
            (groupPending || (manualExpansion[entry.key] ?? false));

          return (
            <div
              key={virtualItem.key}
              ref={rowVirtualizer.measureElement}
              className="absolute left-0 top-0 w-full pb-3"
              data-index={virtualItem.index}
              data-testid="virtual-message-row"
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {entry.type === "group" ? (
                <ToolGroup
                  calls={entry.calls}
                  results={resultsByCall}
                  isLoading={isLoading && isLastMessage}
                  expanded={groupExpanded}
                  onToggle={() =>
                    setManualExpansion((current) => ({
                      ...current,
                      [entry.key]: !groupExpanded,
                    }))
                  }
                  reviewByCall={groupReviews}
                  decisions={decisions}
                  pausedBySubagent={childPending}
                  connectionNames={connectionNames}
                />
              ) : messageType === "human" && message ? (
                <HumanMessage message={message} />
              ) : messageType === "ai" && message ? (
                <AIMessage
                  message={message}
                  // Only pass isLoading to the last message to prevent unnecessary re-renders
                  isLoading={isLastMessage && isLoading}
                  isLastMessage={isLastMessage}
                  toolResultsMappedToToolCall={
                    message.id
                      ? toolResultsMappedToToolCall.get(message.id)
                      : undefined
                  }
                />
              ) : null}
            </div>
          );
        })}
      </div>
      {showAssistantLoading && <AssistantMessageLoading />}
    </>
  );
};
